import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { Download, Loader2 } from "lucide-react";
import { Logo } from "@/components/store/Logo";
import { API } from "@/lib/checkout";

const Shell = ({ children }) => (
  <div className="grain flex min-h-screen flex-col px-5 py-6 sm:px-10">
    <Logo />
    <div className="mx-auto flex w-full max-w-xl flex-1 flex-col justify-center py-16">{children}</div>
  </div>
);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const isRecipient = params.get("gift") === "1";
  const [state, setState] = useState(sessionId ? "checking" : "missing");
  const [isGift, setIsGift] = useState(false);

  useEffect(() => {
    if (!sessionId) return undefined;
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data?.payment_status === "paid") { setIsGift(!!data?.is_gift); setState("paid"); return; }
        if (["expired", "failed"].includes(data?.payment_status)) { setState("failed"); return; }
      } catch { /* keep polling */ }
      if (tries < 15) timer = setTimeout(poll, 2000); else setState("timeout");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <Shell>
      <div data-testid="payment-success-page">
        {state === "checking" && <p className="flex items-center gap-3 text-lg" data-testid="payment-checking"><Loader2 className="h-5 w-5 animate-spin" /> Confirming your payment…</p>}
        {state === "paid" && isGift && !isRecipient && (
          <>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--sage-deep)]">Payment confirmed</p>
            <h1 className="font-display mt-4 text-5xl leading-[1] sm:text-6xl" data-testid="gift-sent-heading">Your gift is on its way.</h1>
            <p className="mt-5 text-[var(--muted)]">We've emailed your friend a link to download the workbook, and sent you a receipt.</p>
            <Link to="/" className="btn-ink mt-10 inline-flex rounded-full px-7 py-4 font-semibold" data-testid="back-to-store-button">Back to the store</Link>
          </>
        )}
        {state === "paid" && (!isGift || isRecipient) && (
          <>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--sage-deep)]">{isRecipient ? "A gift for you" : "Payment confirmed"}</p>
            <h1 className="font-display mt-4 text-5xl leading-[1] sm:text-6xl">{isRecipient ? "Someone thought of your kitchen." : "Your workbook is ready."}</h1>
            <p className="mt-5 text-[var(--muted)]">{isRecipient ? "Your copy of Meal Planning and Grocery Systems is paid for and ready to download." : "Save the PDF now. We've also emailed you a link to this download page."}</p>
            <a href={`${API}/download/${sessionId}`} className="btn-ink mt-10 inline-flex items-center gap-3 rounded-full px-7 py-4 font-semibold" data-testid="download-workbook-button">
              <Download className="h-4 w-4" /> Download the workbook (PDF)
            </a>
          </>
        )}
        {["failed", "timeout", "missing"].includes(state) && (
          <>
            <h1 className="font-display text-5xl leading-[1]" data-testid="payment-not-confirmed">We couldn't confirm the payment.</h1>
            <p className="mt-5 text-[var(--muted)]">If you were charged, refresh this page in a minute. Otherwise you can try checkout again.</p>
            <Link to="/" className="btn-ink mt-10 inline-flex rounded-full px-7 py-4 font-semibold" data-testid="back-to-store-button">Back to the store</Link>
          </>
        )}
      </div>
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell>
      <div data-testid="payment-cancel-page">
        <h1 className="font-display text-5xl leading-[1]">Checkout cancelled.</h1>
        <p className="mt-5 text-[var(--muted)]">No payment was taken. The workbook is still here when you're ready.</p>
        <Link to="/" className="btn-ink mt-10 inline-flex rounded-full px-7 py-4 font-semibold" data-testid="back-to-store-button">Back to the store</Link>
      </div>
    </Shell>
  );
}
